import React from 'react';
import { View } from 'react-native';
import Typography from 'src/components/Text/Text';
import Button from 'src/components/Button/Button';
import Divider from 'src/components/Divider/Divider';
import AmountSelector from './AmountSelector';
import InputField from 'src/components/input/input';
import { styles } from '../screens/Insurance/InsuranceScreen.styles';

interface AutoLiabilityProps {
  limit: number;
  setLimit: (value: number) => void;
  isProducer: boolean;
  personalInjuryProtection: {
    value: 'included' | 'not-included' | null;
    set: (value: 'included' | 'not-included') => void;
    limit: string;
    setLimit: (value: string) => void;
  };
  uninsuredMotorist: {
    value: 'included' | 'not-included' | null;
    set: (value: 'included' | 'not-included') => void;
    limit: string;
    setLimit: (value: string) => void;
  };
  underinsuredMotorist: {
    value: 'included' | 'not-included' | null;
    set: (value: 'included' | 'not-included') => void;
    limit: string;
    setLimit: (value: string) => void;
  };
}

const AutoLiability: React.FC<AutoLiabilityProps> = ({
  limit,
  setLimit,
  isProducer,
  personalInjuryProtection,
  uninsuredMotorist,
  underinsuredMotorist,
}) => (
  <View style={styles.switchSectionsCardContents}>
    <AmountSelector
      label="Auto Liability Limit"
      description="How much coverage would you like for Auto Liability?"
      presets={[750000, 1000000, 1500000]}
      value={limit}
      onChange={setLimit}
      testIDPrefix="insurance-auto-liability"
    />

    <Divider />
    <Typography variant="title2">Personal Injury Protection</Typography>
    <View style={styles.buttonGroupContainer}>
      {(['not-included', 'included'] as const).map((value) => (
        <Button
          testID={`insurance-personal-injury-${value}`}
          key={value}
          title={value === 'not-included' ? 'Not Included' : 'Included'}
          type={personalInjuryProtection.value === value ? 'secondary' : 'tertiary'}
          onPress={() => personalInjuryProtection.set(value)}
          style={styles.switchButton}
        />
      ))}
    </View>
    {isProducer && personalInjuryProtection.value === 'included' && (
      <InputField
        variant="amount"
        label="PIP Custom Amount"
        testID="insurance-pip-limit"
        placeholder="0"
        value={personalInjuryProtection.limit}
        onChange={personalInjuryProtection.setLimit}
      />
    )}

    <Divider />
    <Typography variant="title2">Uninsured Motorist</Typography>
    <View style={styles.buttonGroupContainer}>
      {(['not-included', 'included'] as const).map((value) => (
        <Button
          testID={`insurance-uninsured-motorist-${value}`}
          key={value}
          title={value === 'not-included' ? 'Not Included' : 'Included'}
          type={uninsuredMotorist.value === value ? 'secondary' : 'tertiary'}
          onPress={() => uninsuredMotorist.set(value)}
          style={styles.switchButton}
        />
      ))}
    </View>
    {isProducer && uninsuredMotorist.value === 'included' && (
      <InputField
        variant="amount"
        label="Uninsured Motorist Custom Amount"
        testID="insurance-um-limit"
        placeholder="0"
        value={uninsuredMotorist.limit}
        onChange={uninsuredMotorist.setLimit}
      />
    )}

    {isProducer && (
      <>
        <Divider />
        <Typography variant="title2">Underinsured Motorist</Typography>
        <View style={styles.buttonGroupContainer}>
          {(['not-included', 'included'] as const).map((value) => (
            <Button
              testID={`insurance-underinsured-motorist-${value}`}
              key={value}
              title={value === 'not-included' ? 'Not Included' : 'Included'}
              type={underinsuredMotorist.value === value ? 'secondary' : 'tertiary'}
              onPress={() => underinsuredMotorist.set(value)}
              style={styles.switchButton}
            />
          ))}
        </View>
        {underinsuredMotorist.value === 'included' && (
          <InputField
            variant="amount"
            label="Underinsured Motorist Custom Amount"
            testID="insurance-uim-limit"
            placeholder="0"
            value={underinsuredMotorist.limit}
            onChange={underinsuredMotorist.setLimit}
          />
        )}
      </>
    )}
  </View>
);

export default AutoLiability;
